import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AccountScene } from '@/scenes/Account/AccountScene';
import { BlogScene } from '@/scenes/Blog/_BlogScene';
import { EntryRedirect } from '@/scenes/Blog/_EntryRedirect';
import { DybrContainer } from '@/scenes/Dybr/_DybrScene';
import NotFound from '@/scenes/Dybr/Static/NotFound';
import SettingsPage from '@/scenes/User/SettingsPage';

import config, { SLON_MODE } from '@/configs/dybr';
import { ConversationContainer } from '@/scenes/Dybr/Conversations/Conversation';
import { ConversationList } from '@/scenes/Dybr/Conversations/ConversationList';
import { HeaderContainer } from '@/scenes/Header/_HeaderScene';
import { loadLists } from '@/store/lists/operations';
import { loadNotificationEntries, loadNotifications } from '@/store/notifications/operations';
import { selectNotificationEntryIDs } from '@/store/notifications/selectors';
import { selectIsSignupIncomplete } from '@/store/user/selectors/selectIsSignupIncomplete';
import { loadUserById } from '@/store/user/thunks/loadUserById';

import Loading from '@/components/Shared/Loading';
import { ProduceError } from '@/scenes/Dybr/Service/ProduceError';
import { TestComments } from '@/scenes/Dybr/Service/TestComments';
import ErrorLog from '@/scenes/Dybr/Static/ErrorLog';
import ManagementScenes from '@/scenes/Management/ManagementScenes';
import { ConfirmEmailChange } from '@/scenes/User/Settings/ConfirmEmailChange';
import { useActiveProfile, useTokenState } from '@/store/localStorage/useTokenState';
import { checkTokenThunk } from '@/store/user/thunks/checkTokenThunk';

export const AppWrapper = styled.div`
  min-height: 100vh;
  display: block; /*for IE*/
  min-width: 320px;
  position: relative;
  background: ${p => p.theme.backgroundLighter};
`;

export function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userId, activeProfileId } = useActiveProfile();
  const [token, setToken] = useTokenState();
  const isLoggedIn = Boolean(token);
  const [loading, setLoading] = useState(true);

  const isSignupIncomplete = useSelector(selectIsSignupIncomplete);
  const notificationEntryIds = useSelector(selectNotificationEntryIDs);

  useEffect(() => {
    if (userId && window.umami) {
      window.umami.identify(userId);
    }
    async function fetchData() {
      await dispatch(loadUserById({ token, setToken, userId, activeProfileId, navigate }));
      setLoading(false);
    }
    fetchData();
  }, [dispatch, token, userId, activeProfileId]);

  useEffect(() => {
    if (token && activeProfileId !== '0') {
      dispatch(loadLists({ profileId: activeProfileId, token }));
    }
  }, [dispatch, token, activeProfileId]);

  useEffect(() => {
    dispatch(loadNotifications({ token, activeProfileId }));
  }, [dispatch, token, activeProfileId]);

  useEffect(() => {
    dispatch(loadNotificationEntries({ entryIds: notificationEntryIds, token }));
  }, [dispatch, notificationEntryIds, token]);

  useEffect(() => {
    dispatch(checkTokenThunk({ token, setToken }));
    const interval = setInterval(() => {
      dispatch(checkTokenThunk({ token, setToken }));
    }, config.CHECK_TOKEN_INTERVAL);
    return () => clearInterval(interval);
  }, [dispatch, token]);

  const creatingFirstProfile = isLoggedIn && isSignupIncomplete;

  if (loading) {
    return <Loading />;
  }

  return (
    <AppWrapper data-testid="app-wrapper">
      <Routes>
        <Route path="*" element={<HeaderContainer />} />
      </Routes>

      <Routes>
        <Route path="/*" element={<DybrContainer data-testid="dybr-container" />} />
        {!creatingFirstProfile && <Route path="/blog/:slug/*" element={<BlogScene />} />}
        {!creatingFirstProfile && <Route path="/entry/:eid" element={<EntryRedirect />} />}
        {!creatingFirstProfile && (
          <Route path="/settings">
            <Route index path="*" element={<SettingsPage />} />
          </Route>
        )}
        <Route path="/account/*" element={<AccountScene />} />
        <Route path="/manage/*" element={<ManagementScenes />} />
        <Route path="/errorlog" element={<ErrorLog />} />
        <Route path="/direct" element={<ConversationList />} />
        <Route path="/direct/:targetProfileId" element={<ConversationContainer />} />
        {isLoggedIn && <Route path="/account/confirmation/:emailToken" element={<ConfirmEmailChange />} />}
        {SLON_MODE && <Route path="/error" element={<ProduceError />} />}
        {SLON_MODE && <Route path="/testcomments" element={<TestComments />} />}
        <Route path="*" element={<NotFound reason={'App: Не можем найти такую страницу'} />} />
      </Routes>
    </AppWrapper>
  );
}
